// src/components/Teams/TeamProfile.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Avatar, Chip, Card,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Alert, Skeleton, useTheme, useMediaQuery,
  Divider, Stack, Button, Grid, Pagination
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import { useEventContext } from '../../context/EventContext';
import { getFontSize } from '../Eventos/EventsHeats/utils/eventHelpers';

const TeamProfile = () => {
  const { competitionId, teamName } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));

  const {
    evtData,
    getPlace,
  } = useEventContext();

  const [teamData, setTeamData] = useState(null);
  const [teamAthletes, setTeamAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const athletesPerPage = 10;

  // Decodificar el nombre del equipo
  const decodedTeamName = decodeURIComponent(teamName);

  // Obtener todos los atletas del equipo
  useEffect(() => {
    if (!evtData.length || !decodedTeamName) return;

    const athletes = new Map(); // Usar Map para evitar duplicados
    let totalEvents = 0;
    let completedEvents = 0;

    evtData.forEach(event => {
      event.participants?.forEach(participant => {
        if (participant.affiliation === decodedTeamName) {
          const athleteId = participant.idNumber;
          
          if (!athletes.has(athleteId)) {
            athletes.set(athleteId, {
              idNumber: participant.idNumber,
              fullName: participant.fullName || `${participant.firstName || ''} ${participant.lastName || ''}`.trim(),
              year: participant.year,
              events: [],
              stats: {
                total: 0,
                completed: 0
              }
            });
          }

          const athlete = athletes.get(athleteId);
          const place = getPlace(event.eventNumber, event.round, event.heat, athleteId);
          
          athlete.events.push({
            eventNumber: event.eventNumber,
            eventName: event.eventName,
            round: event.round,
            heat: event.heat,
            place,
            hasResult: place && place !== '-'
          });

          athlete.stats.total++;
          totalEvents++;

          if (place && place !== '-') {
            athlete.stats.completed++;
            completedEvents++;
          }
        }
      });
    });

    const athletesArray = Array.from(athletes.values()).map(athlete => ({
      ...athlete,
      events: athlete.events.sort((a, b) => a.eventNumber - b.eventNumber)
    }));

    // Ordenar atletas por eventos completados y luego por número de atleta
    athletesArray.sort((a, b) => {
      if (a.stats.completed !== b.stats.completed) return b.stats.completed - a.stats.completed;
      return a.idNumber.localeCompare(b.idNumber);
    });

    setTeamAthletes(athletesArray);
    setTeamData({
      name: decodedTeamName,
      totalAthletes: athletesArray.length,
      totalEvents,
      completedEvents
    });
    setLoading(false);
  }, [evtData, decodedTeamName, getPlace]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleAthleteClick = (athleteId) => {
    navigate(`/${competitionId}/athlete/${athleteId}`);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Paginación
  const paginatedAthletes = useMemo(() => {
    const startIndex = (page - 1) * athletesPerPage;
    const endIndex = startIndex + athletesPerPage;
    return teamAthletes.slice(startIndex, endIndex);
  }, [teamAthletes, page]);

  const totalPages = Math.ceil(teamAthletes.length / athletesPerPage);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={200} height={32} />
        </Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width="100%" height={60} />
          <Skeleton variant="text" width="80%" height={40} />
        </Paper>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
        ))}
      </Box>
    );
  }

  if (!teamData || teamAthletes.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mb: 2 }}
        >
          Volver
        </Button>
        <Alert severity="warning" variant="outlined">
          <Typography variant="h6" gutterBottom>
            Equipo no encontrado
          </Typography>
          <Typography variant="body2">
            No se encontraron atletas para el equipo: {decodedTeamName}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: isXs ? 1 : 2,
      maxWidth: '100%',
      backgroundColor: theme.palette.background.default
    }}>
      {/* Header con botón de regreso */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={handleBackClick}
          sx={{ 
            mr: 2,
            backgroundColor: theme.palette.background.paper,
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            fontSize: getFontSize(24, 1, isXs, isSm, isMd)
          }}
        >
          Perfil del Equipo
        </Typography>
      </Box>

      {/* Información del equipo */}
      <Paper
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.secondary.light}15, ${theme.palette.background.paper})`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            sx={{
              width: isXs ? 60 : 80,
              height: isXs ? 60 : 80,
              bgcolor: theme.palette.secondary.main,
              fontSize: isXs ? 24 : 32,
              fontWeight: 'bold',
              mr: 3,
              boxShadow: theme.shadows[3]
            }}
          >
            {teamData.name ? teamData.name.charAt(0).toUpperCase() : <GroupIcon />}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                fontSize: getFontSize(28, 1, isXs, isSm, isMd)
              }}
            >
              {teamData.name}
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={`${teamData.totalAthletes} atletas`}
                variant="filled"
                color="secondary"
                size={isXs ? "small" : "medium"}
                icon={<GroupIcon />}
              />
              <Chip 
                label={`${teamData.totalEvents} eventos`}
                variant="outlined"
                color="primary"
                size={isXs ? "small" : "medium"}
              />
              <Chip 
                label={`${teamData.completedEvents} completados`}
                variant="filled"
                color="success"
                size={isXs ? "small" : "medium"}
              />
            </Box>
          </Box>
        </Box>

        {/* Sin estadísticas adicionales - más limpio */}
        <Divider sx={{ my: 2 }} />
      </Paper>

      {/* Lista de atletas */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 'bold',
              color: theme.palette.primary.main,
              fontSize: getFontSize(18, 1, isXs, isSm, isMd)
            }}
          >
            Atletas del Equipo ({teamAthletes.length})
          </Typography>
        </Box>

        <TableContainer>
          <Table size={isXs ? "small" : "medium"}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                  Atleta
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                  Eventos
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                  Completados
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                  Lugar
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAthletes.map((athlete) => {
                const bestPlace = athlete.events
                  .filter(e => e.place && e.place !== '-')
                  .map(e => parseInt(e.place))
                  .filter(p => !isNaN(p));
                
                const bestPlaceValue = bestPlace.length > 0 ? Math.min(...bestPlace) : null;

                return (
                  <TableRow 
                    key={athlete.idNumber}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                    onClick={() => handleAthleteClick(athlete.idNumber)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: isXs ? 32 : 40,
                            height: isXs ? 32 : 40,
                            bgcolor: theme.palette.primary.light,
                            fontSize: isXs ? 14 : 16,
                            mr: 2
                          }}
                        >
                          {athlete.fullName ? athlete.fullName.charAt(0).toUpperCase() : <PersonIcon />}
                        </Avatar>
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 'medium',
                              fontSize: getFontSize(14, 1, isXs, isSm, isMd)
                            }}
                          >
                            {athlete.fullName}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: getFontSize(12, 1, isXs, isSm, isMd) }}
                          >
                            #{athlete.idNumber}
                            {athlete.year && ` • ${athlete.year}`}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography sx={{ fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                        {athlete.stats.total}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography sx={{ fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}>
                        {athlete.stats.completed}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      {bestPlaceValue ? (
                        <Chip 
                          label={`${bestPlaceValue}°`}
                          size="small"
                          variant="outlined"
                          color={bestPlaceValue <= 3 ? 'warning' : 'default'}
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: getFontSize(12, 1, isXs, isSm, isMd)
                          }}
                        />
                      ) : (
                        <Typography 
                          color="text.secondary"
                          sx={{ fontSize: getFontSize(14, 1, isXs, isSm, isMd) }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        {totalPages > 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`
          }}>
            <Pagination 
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isXs ? "small" : "medium"}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TeamProfile;